import { z } from 'zod';

export const HV_DECISIONS = [
  'undecided',
  'discuss',
  'accepted',
  'accepted_enrolled',
  'accepted_declined',
  'waitpool',
  'not_accepted',
] as const;
export const HvDecision = z.enum(HV_DECISIONS);
export type HvDecision = z.infer<typeof HvDecision>;

export type DecisionColor = 'green' | 'yellow' | 'blue' | 'red' | 'grey' | 'grey_red';

export const DECISION_META: Record<HvDecision, { label: string; color: DecisionColor }> = {
  undecided: { label: 'Undecided', color: 'grey' },
  discuss: { label: 'Discuss', color: 'yellow' },
  accepted: { label: 'Accepted', color: 'green' },
  accepted_enrolled: { label: 'Accepted · Enrolled', color: 'green' },
  accepted_declined: { label: 'Accepted · Declined', color: 'grey_red' },
  waitpool: { label: 'Waitpool', color: 'blue' },
  not_accepted: { label: 'Not Accepted', color: 'red' },
};

export const POST_DECISION_STATES: readonly HvDecision[] = [
  'accepted',
  'accepted_enrolled',
  'accepted_declined',
  'waitpool',
  'not_accepted',
];

export const RECOMMENDATION_TYPES = ['trustee', 'internal', 'g_family', 'current_family_bot'] as const;
export const RecommendationType = z.enum(RECOMMENDATION_TYPES);
export type RecommendationType = z.infer<typeof RecommendationType>;

export const RECOMMENDATION_TYPE_LABELS: Record<RecommendationType, string> = {
  trustee: 'Trustee',
  internal: 'Internal',
  g_family: 'G Family',
  current_family_bot: 'Current Family / BOT',
};

export const RECOMMENDATION_SOURCES = ['google_form_import', 'sheet_import', 'hv_form', 'trustee_portal'] as const;
export const RecommendationSource = z.enum(RECOMMENDATION_SOURCES);
export type RecommendationSource = z.infer<typeof RecommendationSource>;

export const RECOMMENDATION_SOURCE_LABELS: Record<RecommendationSource, string> = {
  google_form_import: 'Google Form import',
  sheet_import: 'Sheet import',
  hv_form: 'HV form',
  trustee_portal: 'Trustee referral',
};

export const SIGNALS = [
  'governance',
  'philanthropy',
  'university_ties',
  'business_influence',
  'mentorship',
] as const;
export const Signal = z.enum(SIGNALS);
export type Signal = z.infer<typeof Signal>;

export const SIGNAL_LABELS: Record<Signal, string> = {
  governance: 'Governance',
  philanthropy: 'Philanthropy',
  university_ties: 'University',
  business_influence: 'Business',
  mentorship: 'Mentorship',
};

export const CAPACITY_BANDS = ['unknown', 'annual', 'leadership', 'major', 'transformational'] as const;
export const CapacityBand = z.enum(CAPACITY_BANDS);
export type CapacityBand = z.infer<typeof CapacityBand>;

export const CAPACITY_LABELS: Record<CapacityBand, string> = {
  unknown: 'Unknown',
  annual: 'Annual (<5-fig)',
  leadership: 'Leadership (5-fig)',
  major: 'Major (6-fig)',
  transformational: 'Transformational (7-fig+)',
};

export const CAPACITY_RANK: Record<CapacityBand, number> = {
  transformational: 4,
  major: 3,
  leadership: 2,
  annual: 1,
  unknown: 0,
};

export const ACTIVITY_TYPES = ['note', 'call', 'meeting', 'email'] as const;
export const ActivityType = z.enum(ACTIVITY_TYPES);
export type ActivityType = z.infer<typeof ActivityType>;

export const ROLES = ['super_admin', 'admissions_admin', 'admissions', 'trustee'] as const;
export const Role = z.enum(ROLES);
export type Role = z.infer<typeof Role>;

export const ROLE_LABELS: Record<Role, string> = {
  super_admin: 'Super admin',
  admissions_admin: 'Admissions admin',
  admissions: 'Admissions',
  trustee: 'Trustee',
};

export const STAFF_ROLES: readonly Role[] = ['super_admin', 'admissions_admin', 'admissions'];

export const RoleList = z
  .array(Role)
  .refine((roles) => new Set(roles.filter((r) => STAFF_ROLES.includes(r))).size <= 1, { message: 'A user can hold only one staff role' });

export const AUTH_METHODS = ['google', 'email_code'] as const;
export const AuthMethod = z.enum(AUTH_METHODS);
export type AuthMethod = z.infer<typeof AuthMethod>;

export const AUTH_METHOD_LABELS: Record<AuthMethod, string> = {
  google: 'Google',
  email_code: 'One-time code',
};

export const ACCESS_REQUEST_STATUSES = ['pending', 'approved', 'denied'] as const;
export const AccessRequestStatus = z.enum(ACCESS_REQUEST_STATUSES);
export type AccessRequestStatus = z.infer<typeof AccessRequestStatus>;

export const REFERRAL_STATUSES = ['draft', 'submitted', 'needs_clarification', 'under_review', 'reviewed', 'archived'] as const;
export const ReferralStatus = z.enum(REFERRAL_STATUSES);
export type ReferralStatus = z.infer<typeof ReferralStatus>;

export const REFERRAL_STATUS_LABELS: Record<ReferralStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  needs_clarification: 'Needs clarification',
  under_review: 'Under review',
  reviewed: 'Reviewed',
  archived: 'Archived',
};

export const HOUSEHOLD_ORIGINS = ['veracross', 'manual_prospect'] as const;
export const HouseholdOrigin = z.enum(HOUSEHOLD_ORIGINS);
export type HouseholdOrigin = z.infer<typeof HouseholdOrigin>;

export const NARRATIVE_SOURCES = ['csv_export', 'manual'] as const;
export const NarrativeSource = z.enum(NARRATIVE_SOURCES);
export type NarrativeSource = z.infer<typeof NarrativeSource>;

export const JOB_TYPES = ['ingest_sftp', 'veracross_sync', 'score_pending', 'recommendation_import'] as const;
export const JobType = z.enum(JOB_TYPES);
export type JobType = z.infer<typeof JobType>;

export const RUN_STATUSES = ['running', 'succeeded', 'partial', 'failed'] as const;
export const RunStatus = z.enum(RUN_STATUSES);
export type RunStatus = z.infer<typeof RunStatus>;

export const ASSESSMENT_STATUSES = ['succeeded', 'failed'] as const;
export const AssessmentStatus = z.enum(ASSESSMENT_STATUSES);
export type AssessmentStatus = z.infer<typeof AssessmentStatus>;

export const SCORE_REQUEST_STATUSES = ['pending', 'processing', 'done', 'failed'] as const;
export const ScoreRequestStatus = z.enum(SCORE_REQUEST_STATUSES);
export type ScoreRequestStatus = z.infer<typeof ScoreRequestStatus>;

export const FEEDBACK_VERDICTS = ['confirm', 'reject'] as const;
export const FeedbackVerdict = z.enum(FEEDBACK_VERDICTS);
export type FeedbackVerdict = z.infer<typeof FeedbackVerdict>;

export const DUPLICATE_STATUSES = ['open', 'merged', 'dismissed'] as const;
export const DuplicateStatus = z.enum(DUPLICATE_STATUSES);
export type DuplicateStatus = z.infer<typeof DuplicateStatus>;

export const HV_BANDS = ['high', 'medium', 'low', 'unscored'] as const;
export const HvBand = z.enum(HV_BANDS);
export type HvBand = z.infer<typeof HvBand>;

export const PIPELINE_LISTS = [
  'all',
  'trustee',
  'internal',
  'g_family',
  'current_family_bot',
  'post_decision',
] as const;
export const PipelineList = z.enum(PIPELINE_LISTS);
export type PipelineList = z.infer<typeof PipelineList>;

export const PIPELINE_LIST_LABELS: Record<PipelineList, string> = {
  all: 'All Profiles',
  trustee: 'Trustee Recommendations',
  internal: 'Internal Recommendations',
  g_family: 'Recommended by G Families',
  current_family_bot: 'Current Families & BOT Rec',
  post_decision: 'Post-Decision List',
};

export const NOTIFICATION_STATUSES = ['pending', 'sent', 'failed', 'suppressed', 'disabled'] as const;
export const NotificationStatus = z.enum(NOTIFICATION_STATUSES);
export type NotificationStatus = z.infer<typeof NotificationStatus>;

export const EMAIL_PROVIDERS = ['smtp', 'gmail', 'fake'] as const;
export const EmailProviderName = z.enum(EMAIL_PROVIDERS);
export type EmailProviderName = z.infer<typeof EmailProviderName>;

export const LLM_PROVIDERS = ['vertex_gemini', 'fake'] as const;
export const LlmProvider = z.enum(LLM_PROVIDERS);
export type LlmProvider = z.infer<typeof LlmProvider>;

export const CHAT_ROLES = ['user', 'assistant'] as const;
export const ChatRole = z.enum(CHAT_ROLES);
export type ChatRole = z.infer<typeof ChatRole>;

export const CHAT_MESSAGE_STATUSES = ['ok', 'refused', 'truncated', 'error'] as const;
export const ChatMessageStatus = z.enum(CHAT_MESSAGE_STATUSES);
export type ChatMessageStatus = z.infer<typeof ChatMessageStatus>;

export const CHAT_FEEDBACK_RATINGS = ['up', 'down'] as const;
export const ChatFeedbackRating = z.enum(CHAT_FEEDBACK_RATINGS);
export type ChatFeedbackRating = z.infer<typeof ChatFeedbackRating>;

export const VERACROSS_GRANTED_SCOPES = [
  'admission.applications:list',
  'admission.applications:read',
  'admission.applicants:read',
  'admission.applications.checklists:list',
  'admission.config.years.checklists:read',
] as const;
